import React, { useMemo } from 'react';
import { CheckboxMenu, type DropMenuCheckboxItem, type DropMenuItem, Icon } from 'venn-ui-kit';
import { type OwnershipItem } from './helpers';
import BaseDropMenu from '../../../../../../venn-ui-kit/src/components/drop-menu/implementations/BaseDropMenu';

export interface OwnershipContextSelectorProps {
  assignedContexts: string[];
  shouldShowOwnershipSelector: boolean;
  showOwnershipSelector: () => void;
  hideOwnershipSelector: () => void;
  ownershipItems: OwnershipItem[];
  onToggle: (item: OwnershipItem) => void;
}

export const MultiOwnershipContextSelector = ({
  assignedContexts,
  shouldShowOwnershipSelector,
  showOwnershipSelector,
  hideOwnershipSelector,
  ownershipItems,
  onToggle,
}: OwnershipContextSelectorProps) => {
  const ownershipLabel = useMemo(() => {
    if (assignedContexts.length === 0) return 'Select ownership';
    if (assignedContexts.length === 1) {
      return ownershipItems.find((item) => item.value === assignedContexts[0])?.label ?? 'Select ownership';
    }
    return `${assignedContexts.length} selected`;
  }, [assignedContexts, ownershipItems]);

  const { filteredItems, checkboxItems } = useMemo(() => {
    const filtered: DropMenuItem[] = [];
    const checkbox: DropMenuCheckboxItem[] = [];
    for (const item of ownershipItems) {
      filtered.push({
        label: item.label,
        value: item.value,
      });
      checkbox.push({
        label: item.label,
        value: item.value,
        checked: item.checked,
        disabled: Boolean(item.disabled),
      });
    }
    return { filteredItems: filtered, checkboxItems: checkbox };
  }, [ownershipItems]);

  const handleCheckboxMenuChange = (updatedItems: DropMenuCheckboxItem[]) => {
    const byValue = new Map(ownershipItems.map((i) => [i.value, i]));
    const changed = updatedItems.find((u) => byValue.get(u.value)?.checked !== u.checked);
    if (!changed) {
      return;
    }
    const original = byValue.get(changed.value);
    if (!original) {
      return;
    }
    onToggle({
      ...original,
      checked: Boolean(changed.checked),
      disabled: Boolean(changed.disabled),
    });
  };

  const handleDropMenuChange = (item: DropMenuItem) => {
    const original = ownershipItems.find((i) => i.value === item.value);
    if (original) {
      onToggle({ ...original, checked: !original.checked, disabled: Boolean(original.disabled) });
    }
  };

  const triggerComponent = (
    expanded: boolean,
    _highlighted: DropMenuItem | undefined,
    toggleMenu: (open?: boolean) => void,
  ) => (
    <button
      type="button"
      className="text-venn-dark-blue hover:text-venn-dark-blue font-bold"
      aria-expanded={expanded}
      onClick={() => toggleMenu()}
    >
      {ownershipLabel}
      <Icon type="chevron-down" className="ml-[4px]" />
    </button>
  );

  const menuComponent = (_highlighted: DropMenuItem | undefined, _forceCollapse: () => void, className?: string) => (
    <div className={className}>
      <CheckboxMenu
        width={250}
        height={200}
        hideSelectAll
        searchable
        searchPlaceholder="Search Ownerships..."
        items={checkboxItems}
        onChange={handleCheckboxMenuChange}
        refreshedStyling
      />
    </div>
  );

  return (
    <div className="py-[8px] text-[12px] text-venn-grey-80 leading-[16px] relative">
      <BaseDropMenu
        usePortal
        openByDefault={shouldShowOwnershipSelector}
        onOpen={showOwnershipSelector}
        onCollapse={hideOwnershipSelector}
        filteredItems={filteredItems}
        onChange={handleDropMenuChange}
        triggerComponent={triggerComponent}
        menuComponent={menuComponent}
        label="Ownership context"
        data-testid="ownership-context-dropmenu"
      />
    </div>
  );
};
